import { getExamesRepository } from '../repositories';
import { ExameItem, ExameColaboradorItem, PrecoColaborador, PrecoEmpresa, ApiError } from './types';
import { VariacaoService } from './variacao.service';

export class LandingExamesService {
  private static get repository() {
    return getExamesRepository();
  }

  /**
   * Lista exames disponíveis para agendamento
   * A API já retorna apenas exames com preços válidos (> 0)
   * @returns Promise com array de exames com preços calculados pela API
   */
  static async getExamesDisponiveis(): Promise<ExameItem[]> {
    try {
      // A API já calcula e filtra exames sem preço válido
      return await this.repository.getExamesDisponiveis();
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Lista exames vinculados a um colaborador
   * A API já retorna apenas exames com preços válidos (> 0)
   * @param colaboradorId - ID do colaborador
   * @param empresaId - ID da empresa (fallback se pedidoId não estiver disponível)
   * @param pedidoId - ID do pedido (prioridade - mais específico, evita dados indevidos)
   * @returns Promise com array de exames do colaborador com preços calculados pela API
   */
  static async getExamesPorColaborador(
    colaboradorId: number,
    empresaId?: number,
    pedidoId?: number
  ): Promise<ExameColaboradorItem[]> {
    try {
      // A API já calcula e filtra exames sem preço válido
      return await this.repository.getExamesPorColaborador(colaboradorId, empresaId, pedidoId);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Vincula um exame a um colaborador
   * @param colaboradorId - ID do colaborador
   * @param exameId - ID do exame
   * @param clinicaId - ID da clínica (opcional)
   * @returns Promise com resultado da operação
   */
  static async vincularExameColaborador(
    colaboradorId: number,
    exameId: number,
    clinicaId?: number
  ): Promise<any> {
    try {
      return await this.repository.vincularExameColaborador(colaboradorId, exameId, clinicaId);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Remove um exame de um colaborador
   * @param colaboradorId - ID do colaborador
   * @param exameId - ID do exame
   * @returns Promise com resultado da operação
   */
  static async desvincularExameColaborador(colaboradorId: number, exameId: number): Promise<any> {
    try {
      return await this.repository.desvincularExameColaborador(colaboradorId, exameId);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Calcula preço total dos exames de um colaborador
   * @param colaboradorId - ID do colaborador
   * @returns Promise com preço do colaborador
   */
  static async calcularPrecoColaborador(colaboradorId: number): Promise<PrecoColaborador> {
    try {
      return await this.repository.calcularPrecoColaborador(colaboradorId);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Calcula preço total de todos os exames da empresa
   * @returns Promise com preço total da empresa
   */
  static async calcularPrecoTotalEmpresa(): Promise<PrecoEmpresa> {
    try {
      return await this.repository.calcularPrecoTotalEmpresa();
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Atualiza data_agendamento e/ou clinica_id de um agendamento
   * Só permite atualizar agendamentos sem pedido_id
   * @param agendamentoId - ID do agendamento
   * @param updateData - Dados para atualizar
   * @returns Promise com agendamento atualizado
   */
  static async updateAgendamento(
    agendamentoId: number,
    updateData: {
      data_agendamento?: string;
      clinica_id?: number;
    }
  ): Promise<any> {
    try {
      return await this.repository.updateAgendamento(agendamentoId, updateData);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }


  /**
   * Trata erros da API
   */
  private static handleApiError(error: any): ApiError {
    if (error.response?.data) {
      return {
        message: error.response.data.message || 'Erro na requisição',
        statusCode: error.response.status,
        error: error.response.data.error,
      };
    }

    return {
      message: 'Erro de conexão com o servidor',
      statusCode: 500,
      error: 'Network Error',
    };
  }
}
